import * as React from 'react';

/**
 * The current instant, re-read on each wall-clock minute boundary.
 *
 * Aligned to the boundary rather than every 60 seconds from mount: a bare
 * interval started mid-minute leaves the printed time up to 59 seconds stale,
 * and the callers print a minute that is supposed to BE the minute the screen
 * was read — the header clock an operator checks a deposit's arrival against,
 * and the identity stamp that has to name the minute a leaked screenshot was
 * taken.
 *
 * `now`, when given, freezes the hook: it returns that instant and starts no
 * timer, which is how a test pins the output instead of racing a clock (§6.8).
 */
export function useMinuteTick(now?: Date): Date {
  const [tick, setTick] = React.useState<Date>(() => now ?? new Date());

  React.useEffect(() => {
    if (now) return;
    let interval: ReturnType<typeof setInterval> | undefined;
    const align = setTimeout(
      () => {
        setTick(new Date());
        interval = setInterval(() => setTick(new Date()), 60_000);
      },
      60_000 - (Date.now() % 60_000),
    );
    return () => {
      clearTimeout(align);
      if (interval) clearInterval(interval);
    };
  }, [now]);

  return now ?? tick;
}
